/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { HSM_BACKEND_REJECT_VALUE_TYPE } from '../common/common.types';
import { toggleShowDrawer } from '../layout';

import { createTemplateV2 } from '@/services/hsm-backend/create-template-v2';
import { getTemplates } from '@/services/hsm-backend/get-templates';
import updateTemplate from '@/services/hsm-backend/put-templates';
import {
  CreateTemplateDTO,
  MediaTemplateType,
  TemplateResponseDto,
  TemplateType,
  StrapiTemplate,
} from '@/services/hsm-backend/types';
import { uploadCampaignMedia } from '@/services/hsm-backend/upload-campaign-media';
import { uploadTemplateMedia } from '@/services/hsm-backend/upload-template-media';

interface FetchTemplatesActionParams {
  websiteId: string;
}

interface CreateTemplateActionParams {
  websiteId: string;
  template: CreateTemplateDTO;
  mediaFile: File | null;
}

export interface DeactivateTemplateActionParams {
  websiteId: string;
  templateName: string;
  newValues: Partial<TemplateResponseDto>;
}

export interface UpdateTemplateMediaActionParams {
  websiteId: string;
  templateName: string;
  templateType: TemplateType;
  file: File;
}

export interface UpdateTemplateDefaultMediaActionParams {
  websiteId: string;
  templateName: string;
  templateType: MediaTemplateType;
  file: File | null;
}

export const fetchTemplatesAction = createAsyncThunk(
  'templates/fetchTemplates',
  async ({ websiteId }: FetchTemplatesActionParams) => {
    const templates: TemplateResponseDto[] = await getTemplates(websiteId);

    return templates;
  }
);

export const createTemplateAction = createAsyncThunk<
  StrapiTemplate,
  CreateTemplateActionParams,
  { rejectValue: HSM_BACKEND_REJECT_VALUE_TYPE }
>(
  'templates/createTemplate',
  async (
    { websiteId, template, mediaFile }: CreateTemplateActionParams,
    thunkAPI
  ) => {
    try {
      const newTemplate: StrapiTemplate = await createTemplateV2({
        websiteId,
        templateBody: template,
        file: mediaFile,
      });

      return newTemplate;
    } catch (error) {
      if ((error as AxiosError)?.response?.data) {
        const rejectValue = (error as AxiosError).response
          ?.data as HSM_BACKEND_REJECT_VALUE_TYPE;

        return thunkAPI.rejectWithValue(rejectValue);
      }

      throw error;
    }
  }
);

export const deactivateTemplateAction = createAsyncThunk(
  'templates/deactivateTemplate',
  async (
    { websiteId, templateName, newValues }: DeactivateTemplateActionParams,
    thunkAPI
  ) => {
    const updatedTemplate: StrapiTemplate = await updateTemplate(
      websiteId,
      templateName,
      newValues
    );

    void thunkAPI.dispatch(fetchTemplatesAction({ websiteId }));
    thunkAPI.dispatch(toggleShowDrawer());

    return updatedTemplate;
  }
);

export const updateTemplateMediaAction = createAsyncThunk(
  'templates/updateTemplateMedia',
  async (
    {
      websiteId,
      templateName,
      templateType,
      file,
    }: UpdateTemplateMediaActionParams,
    thunkAPI
  ) => {
    try {
      const mediaId: string = await uploadTemplateMedia({
        websiteId,
        templateType,
        file,
      });

      const updatedTemplate: StrapiTemplate = await updateTemplate(
        websiteId,
        templateName,
        {
          mediaId,
        }
      );

      void thunkAPI.dispatch(fetchTemplatesAction({ websiteId }));

      return updatedTemplate;
    } catch (error) {
      if ((error as AxiosError)?.response?.data) {
        const rejectValue = (error as AxiosError).response
          ?.data as HSM_BACKEND_REJECT_VALUE_TYPE;

        return thunkAPI.rejectWithValue(rejectValue);
      }
    }
  }
);

export const updateTemplateDefaultMediaAction = createAsyncThunk<
  StrapiTemplate,
  UpdateTemplateDefaultMediaActionParams,
  { rejectValue: HSM_BACKEND_REJECT_VALUE_TYPE }
>(
  'templates/updateTemplateDefaultMedia',
  async ({ websiteId, templateName, templateType, file }, thunkAPI) => {
    try {
      const defaultMedia = file
        ? {
            url: await uploadCampaignMedia({ websiteId, templateType, file }),
            fileName: file.name,
          }
        : null;

      const updatedTemplate = await updateTemplate(websiteId, templateName, {
        defaultMedia,
      });

      void thunkAPI.dispatch(fetchTemplatesAction({ websiteId }));

      return updatedTemplate;
    } catch (error) {
      if ((error as AxiosError)?.response?.data) {
        return thunkAPI.rejectWithValue(
          (error as AxiosError).response?.data as HSM_BACKEND_REJECT_VALUE_TYPE
        );
      }
      throw error;
    }
  }
);
